import CountrySelect from "@/components/country_select";
import TagSelectCountry from "@/components/tag_select_country_by_iso3";
import SourceSelect from "@/components/source_select";
import DatasetSelect from "@/components/dataset_select";
import GeoMap from "@/components/Map";
import {
  viewableColumnTypes,
  getFieldType,
  recordFields,
} from "@/tabular_datasets/recordFields";

import { AgGridVue } from "ag-grid-vue";
import axios from "axios";

export default {
  components: {
    CountrySelect,
    AgGridVue,
    TagSelectCountry,
    SourceSelect,
    DatasetSelect,
    GeoMap,
  },

  props: {
    selectedRecords: {
      type: Array,
    },
    recordsData: {
      type: Array,
    },
    noSelect: {
      type: Boolean,
      default: true,
    },
    fact: {
      type: Object,
      default: {},
    },
  },

  data() {
    return {
      query: { source: null, dataset: [], start_date: null, end_date: null },
      showMap: false,
      loading: false,
      dataTable: [],
      disableSearch: false,
      rangeSelectStarted: false,
      gridOptions: {
        columnTypes: viewableColumnTypes,
        rowSelection: "multiple",
        enableRangeSelection: true,
        suppressRowClickSelection: true, // EVENTS // used to render rows, could be populated by parent as well
        onRowSelected: () => {
          const selectedRows = [];
          this.gridOptions.api.forEachNode(x => {
            if (x.isSelected()) {
              selectedRows.push(x.data);
            }
          });
          this.$emit("updateSelectedRecords", selectedRows);
        },
      },
      rules: { name: [{ required: true, message: "Name is required" }] },
      datasets_fields: ["dataset_name:name", "iso3", "source_id"],
      dataTableColumns: [
        {
          headerName: " ",
          field: "selected",
          width: 30,
          headerCheckboxSelection: true,
          checkboxSelection: true,
          suppressMenu: true,
          sortable: true,
        },
        { headerName: "Dataset", field: "dataset_name", width: 150 },
        { headerName: "Country", field: "iso3", width: 100 },
        ...recordFields.map(f => ({
          headerName: f,
          field: f,
          type: getFieldType(f),
        })),
      ],
    };
  },

  mounted() {
    // set event listener for rangeSelectionChanged
    this.gridOptions.api.addEventListener("rangeSelectionChanged", event => {
      const selectionInfo = event.api.getRangeSelections()[0]; // weird, there is only one obj in array
      if (!this.rangeSelectStarted && event.started) {
        this.rangeSelectStarted = true;
      }
      if (this.rangeSelectStarted) {
        // ready to set selections
        const start = selectionInfo.start.rowIndex;
        const end = selectionInfo.end.rowIndex;
        const from = start > end ? end : start;
        const to = start > end ? start : end;
        event.api.forEachNode(x =>
          x.rowIndex >= from && x.rowIndex <= to ? x.setSelected(true) : 0
        );
      }
      if (this.rangeSelectStarted && event.finished) {
        this.rangeSelectStarted = false;
      }
    });
  },

  methods: {
    hideEmptyColumns() {
      this.dataTableColumns.forEach(c => {
        const isEmpty = this.dataTable.map(d => d[c.field]).every(d => !d);
        this.gridOptions.columnApi.setColumnVisible(c.field, !isEmpty);
      });
    },

    createQueryParams() {
      const { source, dataset, start_date, end_date } = this.query;
      const { iso3, displacement_type } = this.fact;

      return {
        locked_at: "not.is.null",
        iso3: iso3 ? `eq.${iso3}` : undefined,

        displacement_type: displacement_type
          ? `eq.${displacement_type}`
          : undefined,

        source_id: source ? `eq.${source}` : undefined,

        id:
          dataset && dataset.length > 0
            ? `in.(${dataset.join(",")})`
            : undefined,

        "datasets_records.start_date": start_date
          ? `gt.${start_date.toISOString()}`
          : undefined,

        "datasets_records.end_date": end_date
          ? `gt.${end_date.toISOString()}`
          : undefined,
      };
    },

    searchRecords() {
      const selectedRows = this.selectedRecords.map(r => r.tdr_id);
      axios
        .get("/psql/tabular_datasets", {
          params: {
            select:
              `${this.datasets_fields.join(",")},` +
              `tabular_datasets_records(*)`,
            limit: 48,
            ...this.createQueryParams(),
          },
        })
        .then(response => {
          const dataTable = response.data.reduce((a, x) => {
            const { tabular_datasets_records, ...extra } = x;
            return [
              ...a,
              ...tabular_datasets_records.map(x => ({ ...x, ...extra })),
            ];
          }, []);

          this.dataTable = dataTable.map(datum => ({
            ...datum,
            start_date: new Date(datum.start_date),
            end_date: new Date(datum.end_date),
          }));

          setTimeout(() => {
            this.gridOptions.api.forEachNode(x => {
              if (selectedRows.includes(x.data.id)) {
                x.setSelected(true);
              }
            });
          }, 10);
        })
        .catch(error => {
          console.error(error);
        });
    },

    onMapVisibleChange() {
      this.$refs.map.reload();
    },

    mapPoints() {
      if (!this.dataTable) {
        return [];
      }

      const selectedRows = this.selectedRecords.map(r => r.tdr_id);
      const selectedRecords = this.dataTable.filter(d =>
        selectedRows.includes(d.id)
      );
      const points1 = selectedRecords
        .filter(d => d.org_lat && d.org_lon)
        .map(d => ({
          coords: [d.org_lat, d.org_lon],
          type: 1,
        }));

      const points2 = selectedRecords
        .filter(d => d.dst_lat && d.dst_lon)
        .map(d => ({
          coords: [d.dst_lat, d.dst_lon],
          type: 3,
        }));

      return [...points1, ...points2];
    },

    resetQuery() {
      this.dataTable = [];
      this.disableSearch = false;
    },
  },

  watch: {
    recordsData() {
      this.dataTable = [...this.recordsData];
      const selectedRows = this.selectedRecords.map(r => r.tdr_id);
      setTimeout(() => {
        this.gridOptions.api.forEachNode(x => {
          if (selectedRows.includes(x.data.id)) {
            x.setSelected(true);
          }
        });
      }, 10);
    },
  },
};
